import { useEffect } from 'react'

import { queryClient } from '@/providers/QueryClientProvider'
import { useMutation, useQuery } from '@api/hooks'
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog'
import { MarketBasket, MarketBasketSettings, MarketBasketStatus } from '@domain'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { Drawer } from '@mui/material'
import { ContractLoadingKeys } from '@services/contractLoading.api'
import { DirectDealKeys } from '@services/directDeals.api'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { PackConvertKeys } from '@services/packConverts.api'
import { ProgramsKeys } from '@services/programs.api'
import { useDialogs } from '@toolpad/core'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'
import { z } from 'zod'

import { newBasketSettingsSchema, updateBasketSettingsSchema } from './BasketSettings.schema'
import BasketSettingsForm from './BasketSettingsForm'

type Props = {
	open: boolean
	onClose: (values?: MarketBasketSettings) => void
	settings?: MarketBasketSettings
	parentForm?: UseFormReturn<any>
}

type BasketSchema =
	| z.infer<typeof newBasketSettingsSchema>
	| z.infer<typeof updateBasketSettingsSchema>

/**
 * Displays a drawer with a basket's settings.
 */
export default function BasketSettings({
	open,
	onClose,
	settings = null,
	parentForm = null,
}: Props) {
	const { marketBasketId } = useParams()
	const [, setPurchaseId] = useQueryParam('purchaseId', StringParam)
	const dialogs = useDialogs()

	// Note: using the param id to know if this is a new basket or an existing one
	const { data, isLoading } = useQuery({
		queryKey: MarketBasketsKeys.settings(marketBasketId),
		queryFn: () => MarketBasketsApi.settings(marketBasketId),
		enabled: !!marketBasketId,
	})

	const { mutate } = useMutation({
		mutationFn: MarketBasketsApi.updateSettings,
		onMutate: () => {
			// Close the detail panel if it is open. It may try to refetch details using a purchaseId that doesn't exist.
			setPurchaseId(undefined)

			// Optimistically update the market basket status to recalculating
			queryClient.setQueryData(
				MarketBasketsKeys.marketBasket(marketBasketId),
				(oldData: MarketBasket) => ({
					...oldData,
					status: MarketBasketStatus.RECALCULATING,
				})
			)
		},

		// Invalidate queries onSuccess to ensure correct data is fetched after mutation
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: ProgramsKeys.all,
			})
			queryClient.removeQueries({
				queryKey: PackConvertKeys.list(marketBasketId),
			})
			queryClient.removeQueries({
				queryKey: DirectDealKeys.all(marketBasketId),
			})
			queryClient.removeQueries({
				queryKey: ContractLoadingKeys.all(marketBasketId),
			})
		},
	})

	const isNewBasket = !marketBasketId
	const schema = isNewBasket ? newBasketSettingsSchema : updateBasketSettingsSchema

	const methods = useForm<BasketSchema>({
		mode: 'onBlur',
		resolver: zodResolver(schema),
		shouldFocusError: true,
		defaultValues: settings,
	})

	// keep drawer form in sync with parent settings (new basket only)
	useEffect(() => {
		if (!open) return
		if (!isNewBasket) return

		methods.reset(settings, {
			keepDirty: false,
			keepTouched: false,
		})
	}, [open, isNewBasket, settings, methods])

	useEffect(() => {
		// update RHF form with the data from the API, if editing an existing basket
		if (!isLoading && marketBasketId) {
			methods.reset(data)
		}
	}, [data, isLoading, marketBasketId, methods])

	const handleSubmit = async (values: MarketBasketSettings) => {
		// If the basket settings are being edited after creation
		if (marketBasketId) {
			onClose()
			return mutate({
				id: marketBasketId,
				settings: {
					...values,
				},
			})
		}
		// If the basket is being created
		methods.reset(values)
		return onClose(values)
	}

	const handleClose = async () => {
		if (!Object.keys(methods.formState.dirtyFields).length) {
			onClose()
			return
		}

		const close = await dialogs.open(ConfirmDialog, {
			title: 'Discard Changes',
			description: 'You have unsaved changes, close without saving?',
			confirmLabel: 'Yes',
			rejectLabel: 'No',
		})

		if (close) {
			methods.reset(data || settings)
			onClose()
		}
	}

	const formId = 'basket-settings'

	return (
		<Drawer open={open} variant="temporary" anchor="right" onClose={handleClose}>
			<FormProvider {...methods}>
				<form id={formId} onSubmit={methods.handleSubmit(handleSubmit)}>
					<BasketSettingsForm
						loading={isLoading}
						onClose={handleClose}
						mode={isNewBasket ? 'NEW' : 'EDIT'}
						formId={formId}
						parentForm={parentForm}
					/>
				</form>
				{isLowerEnvironment && <DevTool control={methods.control} placement="top-right" />}
			</FormProvider>
		</Drawer>
	)
}
